<template>
    <Card :bordered="false" class="border-radius-0">
        <Form :ref="(e) => (refs.form = e)" layout="horizontal">
            <CardBorder :title="title">
                <TemplateBuilder
                        :rowProp="data.rowProp"
                        :useForm="data.useForm"
                        :fields="data.fields"
                        :skeleton="isLoading"
                        :viewOnly="viewOnly"
                ></TemplateBuilder>
            </CardBorder>
        </Form>
        <CardBorder :title="$t('model.samplingRequest.samplingProducts')" id="cardSamplingProducts" :isLoading="isLoading">
            <SamplingProductList
                    v-if="data && data.useForm"
                    :menuContext="menuContext"
                    :viewOnly="viewOnly"
                    :useForm="data.useForm"
                    :parent="data.useForm?.modelRef"
                    :fetchCriteria="data.samplingProducts?.fetchCriteria"
                    :visibleSearch="false"
                    :isChildren="true"
                    :isRepeat="props.isRepeat"
                    columnTemplate="template2"
            ></SamplingProductList>
        </CardBorder>
        <CardBorder :title="$t('model.samplingRequest.attachments')" :isLoading="isLoading">
            <AttachmentList
                    :menuContext="menuContext"
                    :viewOnly="viewOnly || data.disabledList"
                    :useForm="data.useForm"
                    :parent="data.useForm?.modelRef"
                    :fetchCriteria="data.attachments?.fetchCriteria"
                    :visibleSearch="false"
            ></AttachmentList>
        </CardBorder>
        <CardBorder :title="$t('model.samplingRequest.labels.remark')">
            <TemplateBuilder
                    :rowProp="data.rowProp"
                    :useForm="data.useForm"
                    :fields="data.fieldsRemark"
                    :skeleton="isLoading"
                    :viewOnly="viewOnly"
            ></TemplateBuilder>
        </CardBorder>
        <CardBorder v-if="!data.invisibleConsiderSection" :title="$t('model.samplingRequest.labels.considerResult')">
            <TemplateBuilder
                    :rowProp="data.rowProp"
                    :useForm="data.useForm"
                    :fields="data.fieldsConsiderResult"
                    :skeleton="isLoading"
                    :viewOnly="viewOnly"
            ></TemplateBuilder>
        </CardBorder>
        <CardBorder v-if="data.visibleAnalysisResultSection" :title="$t('model.samplingRequest.labels.analysisResult')">
            <SamplingProductList
                    v-if="data && data.useForm"
                    :menuContext="null"
                    :viewOnly="viewOnly"
                    :useForm="data.useForm"
                    :parent="data.useForm?.modelRef"
                    :visibleSearch="false"
                    :isChildren="true"
                    :isRepeat="props.isRepeat"
                    columnTemplate="template3"
            ></SamplingProductList>
        </CardBorder>
        <ChangeLogCollapse
            class="mt-2"
            v-if="visibleChangeLog && data.id"
            :id="data.id"
            :onCallChangeLog="({ id, page, size }) => onCallChangeLog({ id, page, size, createSearchOneCriteria, callChanges })"
            :mappings="[...data.fields, ...data.fieldsRemark, ...data.fieldsConsiderResult]"
        ></ChangeLogCollapse>
    </Card>
</template>

<script>
import { defineComponent, computed, reactive, defineAsyncComponent } from "vue";
import { state, isLoading, initForm, callChanges, createFormTitle, createSearchOneCriteria } from "./sampling-request.logic";
import { onCallChangeLog } from "../../../../utils/form";
import { Card, Form } from "ant-design-vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { matchMenu } from "../../../../utils/menu-context";
import { state as stateApp } from "../../../../app.logic";
import CardBorder from "../../../../components/card/CardBorder";

export default defineComponent({
    components: {
        Card,
        CardBorder,
        Form,
        TemplateBuilder: defineAsyncComponent(() => import("../../../../components/input/template-builder/TemplateBuilder")),
        ChangeLogCollapse: defineAsyncComponent(() => import("../../../../components/change-log/ChangeLogCollapse")),
        SamplingProductList: defineAsyncComponent(() => import("../sampling-product/SamplingProductList")),
        AttachmentList: defineAsyncComponent(() => import("../../attachment/AttachmentList")),
    },
    props: {
        menuContext: { default: () => matchMenu("/app/sampling/sampling-requests", stateApp.authInfo.menus) },
        viewOnly: { default: false },
        form: {},
        setAppPageHeader: { default: true },
        visibleChangeLog: { default: true },
        isRepeat: { default: false },
    },
    setup(props) {
        const route = useRoute();
        const store = useStore();
        const id = props.form ? props.form.id : route.params?.id;
        const data = reactive({ rowProp: { gutter: 24 }, id, searchSamplingRequestType: null });
        const refs = reactive({ form: null });
        data.samplingProducts = {
            fetchCriteria: {
                page: 0, size: 1000,
                select: ['*', 'refExportAmountUnit.*', 'refCompanyProduct.*', 'refCompanyProduct.refProduct.*',
                    'refCountry1.*', 'refCountry2.*', 'refCompanyCountry1.*', 'refCompanyCountry2.*', 'refCompanyCountry1.refCountry.*', 'refCompanyCountry2.refCountry.*',
                    'refStatus.*', 'refAcceptanceStatus.*', 'refSamplingRequest.refSamplingStatus.*', 'refCompanyDocRejectProduct.*', 'refCompanyDocRejectProduct.refCompanyDocReject.*',
                    'refCompanyDocRejectProduct.refCompanyDocReject.refCompanyDocRejectType.*'],
                sort: [{ name: "createDate", type: "ASC" }],
                children: [
                    {
                        name: 'analysiss', page: 0, size: 1000,
                        select: ['*', 'refLabType.*', 'refExtCenterType.*', 'refDepartment.*', 'refLabExt.*', 'refLabAnalysisStatus.*', 'refLabResultStatus.*'],
                        sort: [{ name: "createDate", type: "ASC" }],
                        where: [{ name: 'refExtCenterType.id', type: 'UUID', operator: '=', value: null }],
                        children: [
                            {
                                name: 'topics', page: 0, size: 1000,
                                select: ['*', 'refLabTopic.*',  'refLabTopic.refLabType.*', 'refLabTopic.refLabTopicUnit.*', 'refTopicAnalysisStatus.*'],
                                where: [{ name: 'transferTos.id', type: 'UUID', operator: '=', value: null }],
                                sort: [{ name: "createDate", type: "ASC" }],
                            },
                            {
                                name: 'transferTos', page: 0, size: 1000,
                                select: ['*', 'refLabType.*', 'refExtCenterType.*', 'refDepartment.*', 'refLabExt.*', 'refLabAnalysisStatus.*', 'refLabResultStatus.*'],
                                sort: [{ name: "createDate", type: "ASC" }],
                                children: [
                                    {
                                        name: 'topics', page: 0, size: 1000,
                                        select: ['*', 'refLabTopic.*',  'refLabTopic.refLabType.*', 'refLabTopic.refLabTopicUnit.*', 'refTopicAnalysisStatus.*'],
                                        sort: [{ name: "createDate", type: "ASC" }],
                                    },
                                ],
                            }
                        ],
                    }
                ],
            },
        };
        if (id) {
            data.samplingProducts.fetchCriteria.where = [{ name: 'refSamplingRequest.id', type: 'UUID', operator: '=', value: id }]
            data.attachments = {
                fetchCriteria: {
                    select: ['*'],
                    page: 0,
                    size: 1000,
                    where: [
                        { name: 'model', type: 'String', operator: '=', value: 'SamplingRequest' },
                        { name: 'refId', type: 'UUID', operator: '=', value: id }
                    ],
                },
            };
        }
        initForm({ props, data, refs, route, store });
        const title = computed(() => createFormTitle({ props, data }));
        return {
            props,
            data,
            refs,
            state,
            title,
            isLoading,
            callChanges,
            createSearchOneCriteria,
            onCallChangeLog,
        };
    },
});
</script>
